import { SquareCheckBigIcon } from "lucide-react";
import Markdown from "react-markdown";
import { Link as RouterLink } from "react-router";
import type { InboxNotification } from "#/api/typesGenerated";
import { Button } from "#/components/Button/Button";
import { Link } from "#/components/Link/Link";
import { relativeTime } from "#/utils/time";
import { InboxAvatar } from "./InboxAvatar";

type InboxItemProps = {
	notification: InboxNotification;
	onMarkNotificationAsRead: (notificationId: string) => void;
};

export const InboxItem: React.FC<InboxItemProps> = ({
	notification,
	onMarkNotificationAsRead,
}) => {
	return (
		<div
			className="flex items-stretch gap-3 p-3 group"
			role="listitem"
			tabIndex={-1}
		>
			<div className="shrink-0">
				<InboxAvatar icon={notification.icon} />
			</div>

			<div className="flex flex-col gap-3 flex-1">
				<Markdown
					className="text-content-secondary prose-sm font-medium wrap-anywhere"
					components={{
						a: ({ node, ...props }) => {
							// Notifications also link back into this deployment via
							// absolute URLs; only other origins open a new tab.
							const isExternal =
								props.href?.startsWith("http") &&
								URL.parse(props.href)?.origin !== location.origin;
							return (
								<Link
									{...props}
									target={isExternal ? "_blank" : undefined}
									rel={isExternal ? "noreferrer" : undefined}
								/>
							);
						},
					}}
				>
					{notification.content}
				</Markdown>
				<div className="flex items-center gap-1">
					{notification.actions.map((action) => {
						return (
							<Button variant="outline" size="sm" key={action.label} asChild>
								<RouterLink
									to={action.url}
									onClick={() => {
										onMarkNotificationAsRead(notification.id);
									}}
								>
									{action.label}
								</RouterLink>
							</Button>
						);
					})}
				</div>
			</div>

			<div className="w-12 flex flex-col items-end shrink-0">
				{notification.read_at === null && (
					<>
						<div className="group-focus-within:hidden group-hover:hidden size-2.5 rounded-full bg-highlight-sky">
							<span className="sr-only">Unread</span>
						</div>

						{/* Visually hidden instead of display: none so it stays in the tab order. */}
						<Button
							onClick={() => onMarkNotificationAsRead(notification.id)}
							className="not-group-hover:not-group-focus-within:sr-only bg-surface-primary"
							variant="outline"
							size="sm"
						>
							<SquareCheckBigIcon />
							mark as read
						</Button>
					</>
				)}

				<span className="mt-auto text-content-secondary text-xs font-medium whitespace-nowrap">
					{relativeTime(new Date(notification.created_at))}
				</span>
			</div>
		</div>
	);
};
